import { createContext, ReactNode, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { setActiveColorMode } from "../utils/muiUtils";

export type ColorMode = "light" | "dark";

export const COLOR_MODE_STORAGE_KEY = "oneh-color-mode";

interface ColorModeContextValue {
    mode: ColorMode;
    setMode: (mode: ColorMode) => void;
    toggleColorMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue>({
    mode: "light",
    setMode: () => {},
    toggleColorMode: () => {},
});

/**
 * Deliberate exception to the "never touch browser storage directly, use useLocalStorage" rule.
 *
 * `useLocalStorage` cannot serve this value for two reasons:
 *  1. It JSON-encodes what it stores, so the mode would land as `"\"dark\""`. The no-flash script
 *     in index.html has to read the same key before the bundle loads and compares the RAW string
 *     (`=== "dark"`); encoding it there too would mean duplicating JSON semantics in an inline
 *     script that must stay a one-liner for its CSP hash.
 *  2. It has no try/catch — reading localStorage throws outright when storage is disabled
 *     (Safari private mode, embedded webviews), which would take down app startup.
 *
 * Keep this reader and the inline script in index.html byte-compatible: both store/read a bare
 * "dark" | "light".
 */
export const readStoredColorMode = (): ColorMode => {
    try {
        return localStorage.getItem(COLOR_MODE_STORAGE_KEY) === "dark" ? "dark" : "light";
    } catch {
        return "light";
    }
};

/**
 * Holds the light/dark selection, persists it to localStorage and mirrors it onto
 * <html data-color-mode="..."> so plain-CSS contexts (index.css --oneh-* variables,
 * the crash page) flip together with the MUI theme. The same attribute is pre-set by
 * the inline no-flash script in index.html before the bundle loads.
 */
export const ColorModeProvider = ({ children }: { children: ReactNode }) => {
    const [mode, setMode] = useState<ColorMode>(readStoredColorMode);

    // Published during render, before any child renders: getActivePalette() consumers (plain utils,
    // AG Grid cell renderers, chart configs) must see the new mode in the SAME commit that swaps the
    // MUI theme. Setting it from an effect leaves them one toggle behind — a mode-mismatched mix of
    // one palette's surfaces and the other's ink.
    setActiveColorMode(mode);

    // Layout effect, not effect: the --oneh-* variables keyed off this attribute would otherwise
    // paint one frame in the outgoing mode.
    useLayoutEffect(() => {
        document.documentElement.dataset.colorMode = mode;
    }, [mode]);

    useEffect(() => {
        try {
            localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
        } catch {
            // storage unavailable (private mode) — the selection still applies for the session
        }
    }, [mode]);

    const value = useMemo(() => ({ mode, setMode, toggleColorMode: () => setMode((m) => (m === "dark" ? "light" : "dark")) }), [mode]);

    return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
};

export const useColorMode = () => useContext(ColorModeContext);
